import { Injectable } from '@angular/core';
import {
  calculateDeliveryCost,
  calculateParcelShippingCost,
  SubsidiaryAddressCoverageRequest,
  shouldFallbackToParcelShipping,
} from '@pedix-workspace/shared-delivery';
import { ShippingCostRequest, ShippingCostResponse } from '@pedix-workspace/shared-delivery';
import { EstablishmentCheckout, ShippingOptionDelivery } from '@pedix-workspace/utils';
import { isEqual } from 'lodash-es';

export type MatchingSubsidiaryForAddress = {
  matches: EstablishmentCheckout[];
};

@Injectable({
  providedIn: 'root',
})
export class DeliveryService {
  private lastSubsidiaryCoverageRequestResult: {
    request: SubsidiaryAddressCoverageRequest;
    result: MatchingSubsidiaryForAddress | null;
  } | null = null;

  async calculateShippingCost(
    shippingCostRequest: ShippingCostRequest,
  ): Promise<ShippingCostResponse | null> {
    let response: ShippingCostResponse | null = null;

    if (
      shippingCostRequest.shippingOptionDelivery?.active &&
      shippingCostRequest.shippingOptionParcel?.active
    ) {
      response = await calculateDeliveryCost(
        shippingCostRequest.shippingOptionDelivery,
        shippingCostRequest,
      );

      if (shouldFallbackToParcelShipping(response)) {
        response = await calculateParcelShippingCost(
          shippingCostRequest.shippingOptionParcel,
          shippingCostRequest,
        );
      }
    } else if (shippingCostRequest.shippingOptionDelivery?.active) {
      response = await calculateDeliveryCost(
        shippingCostRequest.shippingOptionDelivery,
        shippingCostRequest,
      );
    } else if (shippingCostRequest.shippingOptionParcel?.active) {
      response = await calculateParcelShippingCost(
        shippingCostRequest.shippingOptionParcel,
        shippingCostRequest,
      );
    }

    return response;
  }

  async getMatchingSubsidiaryForAddress(request: SubsidiaryAddressCoverageRequest) {
    const { apiBaseUrl, coordinates, establishmentsIds } = request;

    if (
      this.lastSubsidiaryCoverageRequestResult &&
      isEqual(this.lastSubsidiaryCoverageRequestResult.request, request)
    ) {
      return this.lastSubsidiaryCoverageRequestResult.result;
    }

    const response = await fetch(`${apiBaseUrl}/shipping/subsidiaries/address-coverage`, {
      method: 'POST',
      body: JSON.stringify({
        coordinates,
        establishmentsIds,
      }),
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      if (response.status === 404) {
        return 'not-found';
      }
      return null;
    }
    const data = await response.json().then(body => body.data as MatchingSubsidiaryForAddress);

    this.lastSubsidiaryCoverageRequestResult = { request, result: data };

    return data;
  }

  async getSubsidiariesDeliveryZones(
    apiBaseUrl: string,
    establishmentsIds: string[],
  ): Promise<
    { establishmentId: string; shippingOptionDelivery: ShippingOptionDelivery | undefined | null }[]
  > {
    const response = await fetch(`${apiBaseUrl}/shipping/subsidiaries/delivery-zones`, {
      method: 'POST',
      body: JSON.stringify({
        establishmentsIds,
      }),
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch subsidiaries delivery zones: ${response.statusText}`);
    }

    const data = await response.json();

    return data.data;
  }
}
